import { useEffect, useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { descargar } from "@/lib/download";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, ChevronDown, ChevronUp } from "lucide-react";

const KPIS = [
  ["dashboard", "Facturado (Dashboard)", "Pedidos activos"],
  ["libro", "Ingresos (Libro de Cuentas)", "Todo lo recibido"],
  ["retenidos", "Anticipos retenidos", "Pedidos cancelados"],
  ["fisico", "Físico registrado", "Estado de cuenta"],
  ["conciliado", "Conciliado", "Sistema = físico"],
  ["pend_conciliar", "Pendiente de conciliar", "Sin registro físico"],
  ["fisico_sin_pago", "Físico sin pago en sistema", "Pendiente de registrar"],
  ["dom_pend", "Domicilios sin cuadrar", "Cobrado por repartidor"],
];

const Tabla = ({ cols, rows, testid }) => (
  <div className="overflow-x-auto max-h-80 border border-zinc-200 rounded" data-testid={testid}>
    <table className="w-full text-xs">
      <thead className="bg-zinc-100 sticky top-0"><tr>{cols.map(([k, l]) => <th key={k} className="px-2 py-1.5 text-left font-semibold">{l}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-2 py-3 text-zinc-400">Sin registros</td></tr>}
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-zinc-100">
            {cols.map(([k]) => <td key={k} className="px-2 py-1.5 whitespace-nowrap">{k === "monto" || k === "fisico" ? (r[k] == null ? "—" : formatMoney(r[k])) : String(r[k] ?? "")}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ConciliacionIngresos = () => {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/reports/conciliacion-ingresos").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!d) return null;
  const t = d.totales;
  const pendientes = d.pagos.filter((p) => p.estado !== "Conciliado" && p.conciliacion);
  return (
    <Card className="p-5 border-zinc-200 rounded-md space-y-4" data-testid="conc-ingresos">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-xl font-semibold">Conciliación de ingresos (pedido por pedido)</div>
          <div className="text-xs text-zinc-500">Pagos posteriores al cierre {d.ultimo_cierre || "(sin cierre)"}. Sólo lectura: los montos no se igualan.</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setOpen(!open)} data-testid="conc-ingresos-toggle">
            {open ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />} Detalle
          </Button>
          <Button size="sm" className="bg-[#002FA7]" onClick={() => descargar("/reports/conciliacion-ingresos.xlsx", "conciliacion_ingresos.xlsx").catch((e) => toast.error(String(e.message || e)))} data-testid="conc-ingresos-excel">
            <Download className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {KPIS.map(([k, l, s]) => (
          <div key={k} className="border border-zinc-200 rounded p-3" data-testid={`conc-ing-${k}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{l}</div>
            <div className="font-black text-lg">{formatMoney(t[k])}</div>
            <div className="text-[10px] text-zinc-400">{s}</div>
          </div>
        ))}
      </div>
      <div className="text-xs text-zinc-600" data-testid="conc-ing-explica">
        Libro − Facturado = {formatMoney(t.libro - t.dashboard)} (anticipos retenidos {formatMoney(t.retenidos)}) · Libro − Físico = {formatMoney(t.libro - t.fisico)} (pendiente de conciliar {formatMoney(t.pend_conciliar)} − físico sin pago {formatMoney(t.fisico_sin_pago)})
      </div>
      {open && (
        <div className="space-y-4">
          <div className="text-sm font-semibold">Pagos pendientes de conciliar ({pendientes.length})</div>
          <Tabla testid="conc-ing-pendientes" rows={pendientes} cols={[["pedido", "Pedido"], ["cliente", "Cliente"], ["estado_pedido", "Estado pedido"], ["factura", "Factura"], ["tipo", "Tipo"], ["fecha_pago", "Fecha pago"], ["metodo", "Método"], ["banco", "Banco"], ["monto", "Monto"], ["fisico", "Físico"], ["estado", "Estado"], ["observacion", "Observación"]]} />
          <div className="text-sm font-semibold">Registros físicos sin pago en sistema ({d.fisico_sin_pago.length})</div>
          <Tabla testid="conc-ing-fisico-sin" rows={d.fisico_sin_pago} cols={[["fecha", "Fecha"], ["pedido", "Pedido"], ["monto", "Monto"], ["metodo", "Método"], ["banco", "Banco"], ["autorizacion", "Autorización"], ["concepto", "Concepto"]]} />
          <div className="text-sm font-semibold">Cobros de App Domicilios sin cuadrar ({d.domicilio_pendiente.length})</div>
          <Tabla testid="conc-ing-dom" rows={d.domicilio_pendiente} cols={[["pedido", "Pedido"], ["cliente", "Cliente"], ["fecha", "Fecha"], ["metodo", "Método"], ["banco", "Banco"], ["monto", "Monto"]]} />
        </div>
      )}
    </Card>
  );
};
